// pages/About/About.jsx - About Us page fully consistent with DevLearn branding
import { ArrowRight, Users, Target, Award } from 'lucide-react';
import './About.css'; // Dedicated About page CSS
import '../../index.css'; // Shared base styles

const About = () => {
  return (
    <>
      {/* Hero */}
      <section className="hero bg-light">
        <div className="container text-center">
          <h1 className="text-primary">About DevLearn Impact Research Ltd</h1>
          <p>
            A Zambian consulting firm registered on June 28, 2025, providing technical and advisory services in MERL, research, project management, and capacity building. Committed to evidence-led processes for sustainable development in Zambia and Africa.
          </p>
          <div className="hero-buttons">
            <a href="#values" className="btn btn-primary">
              Our Values <ArrowRight />
            </a>
            <a href="#approach" className="btn btn-secondary">
              Our Approach
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section overview">
        <div className="container text-center">
          <h2 className="text-primary">Who We Are</h2>
          <p className="lead">
            DevLearn empowers communities through evidence-driven solutions delivered by dedicated local and international professionals. We promote inclusive, evidence-informed decision-making, working with governments, NGOs, donors, and community-based organizations to foster sustainable transformation.
          </p>

          <div className="overview-grid">
            <div className="overview-card">
              <div className="overview-icon icon-primary">
                <Target size={48} />
              </div>
              <h3 className="text-primary">Our Mission</h3>
              <p>
                To empower communities through evidence-driven development solutions delivered by a dedicated team committed to learning, collaboration, and impact.
              </p>
            </div>
            <div className="overview-card">
              <div className="overview-icon icon-primary">
                <Users size={48} />
              </div>
              <h3 className="text-primary">Our Vision</h3>
              <p>
                To become a leading, locally rooted hub for world-class social and development research and training, working collaboratively to drive evidence-based change.
              </p>
            </div>
            <div className="overview-card">
              <div className="overview-icon icon-primary">
                <Award size={48} />
              </div>
              <h3 className="text-primary">Our Impact</h3>
              <p>
                Strengthening capacities in MERL and data systems to support better-informed, equity-driven decisions, leading to sustainable development and systems transformation across Zambia and Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="values" className="section values bg-light">
        <div className="container text-center">
          <h2 className="text-primary">Our Core Values</h2>
          <p className="lead">
            These principles guide our work, ensuring integrity, collaboration, and community empowerment in every initiative.
          </p>
          <div className="values-grid">
            <div className="value-card"><h3 className="text-primary">Integrity</h3><p>We uphold the highest standards of honesty, ethics, and professionalism.</p></div>
            <div className="value-card"><h3 className="text-primary">Collaboration</h3><p>We foster strong partnerships and shared learning worldwide.</p></div>
            <div className="value-card"><h3 className="text-primary">Excellence</h3><p>We deliver high-quality services and solutions that exceed expectations.</p></div>
            <div className="value-card"><h3 className="text-primary">Innovation</h3><p>We continuously evolve our approaches through creativity and experimentation.</p></div>
            <div className="value-card"><h3 className="text-primary">Inclusion & Diversity</h3><p>We embrace diverse perspectives and promote equitable opportunities for all.</p></div>
            <div className="value-card"><h3 className="text-primary">Evidence & Learning</h3><p>We use data and research to inform decisions, drive learning, and enhance outcomes.</p></div>
            <div className="value-card"><h3 className="text-primary">Community Empowerment</h3><p>We centre our work on local ownership, capacity building, and sustainable impact.</p></div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="section approach">
        <div className="container text-center">
          <h2 className="text-primary">Our Approach</h2>
          <p className="lead">
            Guided by three interlinked pillars: Facilitation, Linkage, and Partnership. We act as catalysts for change, connecting research to practice through tailored, pragmatic methods and co-creating solutions with stakeholders.
          </p>
          <div className="approach-grid">
            <div className="approach-step"><h3 className="text-primary">Facilitation</h3><p>We act as catalysts for change, facilitating transformation rather than imposing solutions.</p></div>
            <div className="approach-step"><h3 className="text-primary">Linkage</h3><p>We connect research to practice through tailored, context-specific methods.</p></div>
            <div className="approach-step"><h3 className="text-primary">Partnership</h3><p>We co-create solutions with all stakeholders, promoting joint ownership and mutual learning.</p></div>
          </div>
        </div>
      </section>

      {/* Theory of Change Timeline */}
      <section className="section timeline bg-light">
        <div className="container text-center">
          <h2 className="text-primary">Our Theory of Change</h2>

<p className="lead">
  At DevLearn, we believe that evidence-led processes, when locally owned and inclusively executed,
  can transform systems and improve lives. If we strengthen the capacity of local
  institutions, co-create robust data systems, and facilitate inclusive stakeholder
  engagement using rigorous research and MERL methodologies, THEN development actors
  will make better-informed, context-specific, and equity-driven decisions, WHICH leads
  to more accountable governance, improved service delivery, and empowered communities.
  Ultimately, this contributes to sustainable and inclusive development, as well as
  systems transformation, in Zambia and across Africa.
</p>

<p className="fw-semibold mt-4">
  Our Theory of Change is anchored in:
</p>

<ul className="list-unstyled row g-3">
  <li className="col-12 col-md-4">
    <strong>Local Ownership:</strong> Communities and partners shape the change they seek.
  </li>
  <li className="col-12 col-md-4">
    <strong>Evidence-Based Practice:</strong> High-quality data, analysis, and reflection inform action.
  </li>
  <li className="col-12 col-md-4">
    <strong>Inclusive Development:</strong> Gender equity, youth voice, and social inclusion are embedded at every step.
  </li>
</ul>

<br/>

          {/* <h2 className="text-primary">Our Theory of Change</h2>
          <p className="lead">At DevLearn, evidence-led processes, when locally owned and inclusively executed, can transform systems and improve lives. If we strengthen the capacity of local institutions, co-create robust data systems, and facilitate inclusive stakeholder engagement using rigorous research and MERL methodologies, THEN development actors will make better-informed, context-specific, and equity-driven decisions, WHICH leads to more accountable governance, improved service delivery, and empowered communities. Ultimately, this contributes to sustainable and inclusive development, as well as systems transformation, in Zambia and across Africa..</p> */}

          <div className="timeline-container">
            {[
              { year: 'Inputs', title: 'Skilled Team & Tools', content: 'Multidisciplinary experts, digital tools (STATA, R, KoboToolbox), partnerships with NGOs/government/donors, and local knowledge.' },
              { year: 'Activities', title: 'Research & Capacity Building', content: 'Conduct applied research, design MERL systems, provide technical assistance, and facilitate participatory learning events.' },
              { year: 'Outputs', title: 'Evidence & Capacities', content: 'Evidence-based insights, strengthened MERL frameworks, capacitated stakeholders, and co-created learning tools.' },
              { year: 'Outcomes', title: 'Inclusive Decision-Making', content: 'More inclusive, adaptive decisions; strengthened accountability; empowered actors; scalable solutions adopted.' },
              { year: 'Impact', title: 'Sustainable Development', content: 'Sustainable outcomes through local ownership; improved governance, service delivery, and social equity across sectors.' }
            ].map((item, i) => (
              <div key={i} className="timeline-item">
                <div className="timeline-year">{item.year}</div>
                <div className="timeline-content">
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="final-cta text-center">
        <div className="container">
          <h2>Ready to Partner for Change?<br />Let's Drive Evidence-Based Impact Together.</h2>
          <a href="/contact" className="btn btn-primary">
            Contact Us <ArrowRight size={28} />
          </a>
        </div>
      </section>

      {/* Footer - Reusable */}
      <footer className="footer">
        <div className="footer-grid">
          <div><div className="footer-logo">DevLearn Impact Research Ltd</div></div>
          <div>
            <h4>Services</h4>
            <ul>
              <li><a href="/services">Applied Research</a></li>
              <li><a href="/services">MERL Frameworks</a></li>
              <li><a href="/services">Capacity Building</a></li>
            </ul>
          </div>
          <div>
            <h4>Thematic Areas</h4>
            <ul>
              <li><a href="/sectors">Education</a></li>
              <li><a href="/sectors">Public Health</a></li>
              <li><a href="/sectors">Finance & Development</a></li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li><a href="/about">About</a></li>
              <li><a href="/team">Team</a></li>
              <li><a href="/reports">Reports</a></li>
              <li><a href="/locations">Locations</a></li>

              {/* <li><a href="#">About</a></li>
              <li><a href="#">Team</a></li>
              <li><a href="#">Reports</a></li> */}
            </ul>
          </div>
          <div>
            <h4>Contact</h4>
            <ul>
              <li><a href="mailto:info@devlearnimpact.com">info@devlearnimpact.com</a></li>
              <li><a href="/locations">Locations</a></li>
            </ul>
          </div>
        </div>
        <div className="copyright">© 2025 DevLearn Impact Research Ltd. All rights reserved.</div>
      </footer>
    </>
  );
};

export default About;


// // pages/About/About.jsx - About Us page for DevLearn (adapted from PDF pages 3-5, 6, 11-13, 15)
// import { ArrowRight, Users, Target, Clock, Award } from 'lucide-react';
// import './About.css'; // Dedicated About page CSS
// import '../../index.css'; // Import shared base styles

// const About = () => {
//   return (
//     <>
//       {/* Hero */}
//       <section className="hero" style={{ background: 'linear-gradient(180deg, #F0F9FF 0%, #ffffff 100%)' }}>
//         <div className="container">
//           <h1 style={{ color: '#1E3A8A' }}>About DevLearn Impact Research Ltd</h1>
//           <p>
//             A Zambian consulting firm registered on June 28, 2025, providing technical and advisory services in MERL, research, project management, and capacity building. Committed to evidence-led processes for sustainable development in Zambia and Africa.
//           </p>
//           <div className="hero-buttons">
//             <a href="#values" className="btn btn-primary">
//               Our Values <ArrowRight />
//             </a>
//             <a href="#approach" className="btn btn-secondary">
//               Our Approach
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Overview */}
//       <section className="section overview">
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Who We Are</h2>
//             <p className="lead">
//               DevLearn empowers communities through evidence-driven solutions delivered by dedicated local and international professionals. We promote inclusive, evidence-informed decision-making, working with governments, NGOs, donors, and community-based organizations to foster sustainable transformation.
//             </p>
//           </div>
//           <div className="overview-grid">
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Target size={48} style={{ color: '#0D9488' }} />
//               </div>
//               <h3 style={{ color: '#1E3A8A' }}>Our Mission</h3>
//               <p>
//                 To empower communities through evidence-driven development solutions delivered by a dedicated team of local and international professionals committed to learning, collaboration, and impact.
//               </p>
//             </div>
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Users size={48} style={{ color: '#0D9488' }} />
//               </div>
//               <h3 style={{ color: '#1E3A8A' }}>Our Vision</h3>
//               <p>
//                 To become a leading, locally rooted hub for world-class social and development research and training, working collaboratively with local and international professionals to drive evidence-based change.
//               </p>
//             </div>
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Award size={48} style={{ color: '#0D9488' }} />
//               </div>
//               <h3 style={{ color: '#1E3A8A' }}>Our Impact</h3>
//               <p>
//                 Strengthening capacities in MERL and data systems to support better-informed, equity-driven decisions, leading to sustainable development and systems transformation across Zambia and Africa.
//               </p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Values */}
//       <section id="values" className="section values bg-light" style={{ background: '#F0F9FF' }}>
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Our Core Values</h2>
//             <p className="lead">
//               These principles guide our work, ensuring integrity, collaboration, and community empowerment in every initiative.
//             </p>
//           </div>
//           <div className="values-grid">
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Integrity</h3>
//               <p>We uphold the highest standards of honesty, ethics, and professionalism in all we do.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Collaboration</h3>
//               <p>We foster strong partnerships and shared learning with clients, communities, and professionals worldwide.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Excellence</h3>
//               <p>We deliver high-quality services and solutions that exceed expectations and drive meaningful change.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Innovation</h3>
//               <p>We continuously evolve our approaches through creativity, experimentation, and learning.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Inclusion and Diversity</h3>
//               <p>We embrace diverse perspectives and promote equitable opportunities for all.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Evidence and Learning</h3>
//               <p>We use data and research to inform decisions, drive learning, and enhance development outcomes.</p>
//             </div>
//             <div className="value-card">
//               <h3 style={{ color: '#1E3A8A' }}>Community Empowerment</h3>
//               <p>We centre our work on local ownership, capacity building, and sustainable impact.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Approach */}
//       <section id="approach" className="section approach">
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Our Approach</h2>
//             <p className="lead">
//               Guided by three interlinked pillars: Facilitation, Linkage, and Partnership. We act as catalysts for change, connecting research to practice through tailored, pragmatic methods and co-creating solutions with stakeholders.
//             </p>
//           </div>
//           <div className="approach-grid">
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>Facilitation</h3>
//               <p>We act as catalysts for change, facilitating transformation rather than imposing solutions.</p>
//             </div>
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>Linkage</h3>
//               <p>We connect research to practice through tailored, pragmatic, and context-specific methods.</p>
//             </div>
//             <div className="approach-step">
//               <h3 style={{ color: '#1E3A8A' }}>Partnership</h3>
//               <p>We co-create solutions with all stakeholders, promoting joint ownership and mutual learning.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Theory of Change Timeline */}
//       <section className="section timeline bg-light" style={{ background: '#F0F9FF' }}>
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Our Theory of Change</h2>
//             <p className="lead">
//               Evidence-led processes, locally owned and inclusively executed, transform systems and improve lives.
//             </p>
//           </div>
//           <div className="timeline-container">
//             <div className="timeline-item">
//               <div className="timeline-year">Inputs</div>
//               <div className="timeline-content">
//                 <h3>Skilled Team & Tools</h3>
//                 <p>Multidisciplinary experts, digital tools (STATA, R, KoboToolbox), partnerships with NGOs/government/donors, and local knowledge.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">Activities</div>
//               <div className="timeline-content">
//                 <h3>Research & Capacity Building</h3>
//                 <p>Conduct applied research, design MERL systems, provide technical assistance, and facilitate participatory learning events.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">Outputs</div>
//               <div className="timeline-content">
//                 <h3>Evidence & Capacities</h3>
//                 <p>Evidence-based insights, strengthened MERL frameworks, capacitated stakeholders, and co-created learning tools.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">Outcomes</div>
//               <div className="timeline-content">
//                 <h3>Inclusive Decision-Making</h3>
//                 <p>More inclusive, adaptive decisions; strengthened accountability; empowered actors; scalable solutions adopted.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">Impact</div>
//               <div className="timeline-content">
//                 <h3>Sustainable Development</h3>
//                 <p>Sustainable outcomes through local ownership; improved governance, service delivery, and social equity across sectors.</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center" style={{ background: '#1E3A8A', color: '#fff' }}>
//         <div className="container">
//           <h2>
//             Ready to Partner for Change?<br />Let's Drive Evidence-Based Impact Together.
//           </h2>
//           <a href="/contact" className="btn btn-primary" style={{ background: '#fff', color: '#1E3A8A' }}>
//             Contact Us <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer - Reused with DevLearn details */}
//       <footer className="footer" style={{ background: '#111827' }}>
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo" style={{ color: '#0D9488' }}>DevLearn Impact Research Ltd</div>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education</a></li>
//               <li><a href="/sectors">Public Health</a></li>
//               <li><a href="/sectors">Finance & Development</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Company</h4>
//             <ul>
//               <li><a href="#">About</a></li>
//               <li><a href="#">Team</a></li>
//               <li><a href="#">Reports</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Contact</h4>
//             <ul>
//               <li><a href="#">info@devlearnimpact.com</a></li>
//               <li><a href="#">Locations</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright" style={{ color: '#9CA3AF' }}>
//           © 2025 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default About;

// // pages/About/About.jsx - Updated to import separate CSS
// import { ArrowRight, Users, Target, Clock, Award } from 'lucide-react';
// import './About.css'; // Import the dedicated About page CSS
// import '../../index.css'; // Import shared base styles (adjust path if needed)

// const About = () => {
//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container">
//           <h1>About DevLearn Research</h1>
//           <p>
//             Pioneering evidence-based DevLearns for a better world. Since 2015, we've empowered organizations
//             with research that informs, inspires, and ignites change.
//           </p>
//           <div className="hero-buttons">
//             <a href="#team" className="btn btn-primary">
//               Meet Our Team <ArrowRight />
//             </a>
//             <a href="#values" className="btn btn-secondary">
//               Our Values
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Overview */}
//       <section className="section overview">
//         <div className="container">
//           <div className="text-center">
//             <h2>Who We Are</h2>
//             <p className="lead">
//               DevLearn Research is a global leader in applied research, specializing in impact evaluation, policy analysis,
//               and strategic consulting. We bridge the gap between data and decision-making, serving governments, NGOs,
//               and institutions across education, health, finance, and beyond.
//             </p>
//           </div>
//           <div className="overview-grid">
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Target size={48} />
//               </div>
//               <h3>Our Mission</h3>
//               <p>
//                 To deliver rigorous, actionable research that drives equitable outcomes and sustainable progress.
//               </p>
//             </div>
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Users size={48} />
//               </div>
//               <h3>Our Vision</h3>
//               <p>
//                 A world where every decision is informed by evidence, fostering innovation and inclusion.
//               </p>
//             </div>
//             <div className="overview-card">
//               <div className="overview-icon">
//                 <Award size={48} />
//               </div>
//               <h3>Our Impact</h3>
//               <p>
//                 Over 500 studies completed, influencing policies in 20+ countries and serving 150+ clients with 98% satisfaction.
//               </p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Values */}
//       <section id="values" className="section values bg-light">
//         <div className="container">
//           <div className="text-center">
//             <h2>Our Core Values</h2>
//             <p className="lead">
//               These principles guide every project, ensuring integrity, excellence, and collaboration.
//             </p>
//           </div>
//           <div className="values-grid">
//             <div className="value-card">
//               <h3>Integrity</h3>
//               <p>Transparent methodologies and ethical practices in every DevLearn we uncover.</p>
//             </div>
//             <div className="value-card">
//               <h3>Excellence</h3>
//               <p>Relentless pursuit of precision, from data collection to strategic recommendations.</p>
//             </div>
//             <div className="value-card">
//               <h3>Collaboration</h3>
//               <p>Partnering closely with clients to co-create solutions that resonate and endure.</p>
//             </div>
//             <div className="value-card">
//               <h3>Innovation</h3>
//               <p>Embracing cutting-edge tools and approaches to stay ahead in a dynamic world.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* History Timeline */}
//       <section className="section timeline">
//         <div className="container">
//           <div className="text-center">
//             <h2>Our Journey</h2>
//             <p className="lead">
//               From humble beginnings to global influence, here's how we've evolved.
//             </p>
//           </div>
//           <div className="timeline-container">
//             <div className="timeline-item">
//               <div className="timeline-year">2015</div>
//               <div className="timeline-content">
//                 <h3>Founded in Nairobi</h3>
//                 <p>Started as a boutique firm focused on African development research.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">2018</div>
//               <div className="timeline-content">
//                 <h3>Expanded to Asia & Europe</h3>
//                 <p>Launched international projects, building a diverse expert network.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">2021</div>
//               <div className="timeline-content">
//                 <h3>Impact Evaluation Leadership</h3>
//                 <p>Awarded for pioneering randomized control trials in public health.</p>
//               </div>
//             </div>
//             <div className="timeline-item">
//               <div className="timeline-year">2024</div>
//               <div className="timeline-content">
//                 <h3>Global Scale-Up</h3>
//                 <p>Reached 20+ countries, integrating AI-driven analytics for deeper DevLearns.</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* Team */}
//       <section id="team" className="section team">
//         <div className="container">
//           <div className="text-center">
//             <h2>Our Leadership Team</h2>
//             <p className="lead">
//               A blend of academic rigor, practical expertise, and visionary thinking.
//             </p>
//           </div>
//           <div className="team-grid">
//             <div className="team-card">
//               <div className="team-image"></div>
//               <h3>Dr. Aisha Mwangi</h3>
//               <p>Founder & CEO</p>
//               <p>PhD in Development Economics; 15+ years shaping policy in Africa and beyond.</p>
//             </div>
//             <div className="team-card">
//               <div className="team-image"></div>
//               <h3>Prof. Liam Chen</h3>
//               <p>Chief Research Officer</p>
//               <p>Expert in quantitative methods; led 200+ studies across health and education sectors.</p>
//             </div>
//             <div className="team-card">
//               <div className="team-image"></div>
//               <h3>Sarah Patel</h3>
//               <p>Director of Operations</p>
//               <p>Strategic partnerships specialist; scaled operations to 50+ global collaborators.</p>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>
//             Join us in turning DevLearns into action.<br />Let's shape the future together.
//           </h2>
//           <a href="#" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Reuse Footer from App */}
//       <footer className="footer">
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo">DevLearn Research</div>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="#">Market Research</a></li>
//               <li><a href="#">Impact Evaluation</a></li>
//               <li><a href="#">Data Analytics</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Sectors</h4>
//             <ul>
//               <li><a href="#">Education</a></li>
//               <li><a href="#">Public Health</a></li>
//               <li><a href="#">Finance & Development</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="#">About</a></li>
//               <li><a href="#">Team</a></li>
//               <li><a href="#">Reports</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Contact</h4>
//             <ul>
//               <li><a href="#">hello@DevLearnresearch.com</a></li>
//               <li><a href="#">Locations</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2025 DevLearn Research. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default About;